import type { PlatformError } from "@storytell/cp-client";
import type { ConversationResponse } from "@storytell/cp-client/src/gen/chat/v3/chat_pb";
import type { Result } from "neverthrow";

const TTL_MS = 5 * 60 * 1000;
const MAX_ENTRIES = 10;

type Entry = { response: ConversationResponse; fetchedAt: number };

type ConversationFetch = Promise<Result<ConversationResponse, PlatformError>>;

/**
 * Raw ConversationResponse cache keyed by chatId. Holds only the network
 * payload so a settled chat can be replayed through processChatRecording
 * without a round-trip. Granular/streaming stores are never touched here —
 * the active chat's content stays owned by the resource fetcher.
 *
 * Fetches go through load, which shares one in-flight request per chat, so
 * opening a chat whose prefetch is still pending waits for that request
 * instead of issuing a second one.
 */
export const createChatPrefetchCache = (fetchConversation: (chatId: string) => ConversationFetch) => {
  const entries = new Map<string, Entry>();
  const pending = new Map<string, ConversationFetch>();

  const get = (chatId: string): ConversationResponse | undefined => {
    const entry = entries.get(chatId);
    if (!entry) return undefined;
    if (Date.now() - entry.fetchedAt >= TTL_MS) {
      entries.delete(chatId);
      return undefined;
    }
    // Re-seat as newest so a chat the user keeps returning to outlives one warmed and never opened.
    entries.delete(chatId);
    entries.set(chatId, entry);
    return entry.response;
  };

  const ageMs = (chatId: string): number | undefined => {
    const entry = entries.get(chatId);
    return entry ? Date.now() - entry.fetchedAt : undefined;
  };

  const set = (chatId: string, response: ConversationResponse) => {
    // Map keeps insertion order and updating a value in place won't refresh it,
    // so delete first to re-seat the key as newest for LRU eviction.
    entries.delete(chatId);
    entries.set(chatId, { response, fetchedAt: Date.now() });
    while (entries.size > MAX_ENTRIES) {
      const oldest = entries.keys().next();
      if (oldest.done) break;
      entries.delete(oldest.value);
    }
  };

  const has = (chatId: string): boolean => get(chatId) !== undefined;

  const load = (chatId: string): ConversationFetch => {
    const inFlight = pending.get(chatId);
    if (inFlight) return inFlight;

    const request = fetchConversation(chatId);
    pending.set(chatId, request);
    // clear and delete drop the pending entry, so a request that outlived its
    // project or its chat finds itself untracked and isn't cached.
    const settle = () => {
      const current = pending.get(chatId) === request;
      if (current) pending.delete(chatId);
      return current;
    };
    const cache = async (): Promise<void> => {
      let res: Awaited<ConversationFetch>;
      try {
        res = await request;
      } catch {
        settle();
        return;
      }
      // A snapshot taken mid-stream is never served — opening a streaming chat
      // always refetches — so don't spend a cache slot on it.
      if (settle() && res.isOk() && !res.value.streamingState?.activeMessageId) set(chatId, res.value);
    };
    void cache();
    return request;
  };

  const del = (chatId: string) => {
    entries.delete(chatId);
    pending.delete(chatId);
  };

  const clear = () => {
    entries.clear();
    pending.clear();
  };

  return { get, ageMs, set, has, load, delete: del, clear };
};

export type ChatPrefetchCache = ReturnType<typeof createChatPrefetchCache>;
